import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Divider,
  useColorModeValue,
  useToast,
  Spinner,
  Code,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CopyIcon, RepeatIcon } from '@chakra-ui/icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from 'contexts/AuthContext';

const ApiKeyModal = ({ isOpen, onClose }) => {
  const { currentUser, userData } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  const functions = getFunctions();

  // Color mode values
  const bgColor = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const codeColor = useColorModeValue('gray.800', 'gray.100');
  const codeBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  // Load existing API key from userData
  useEffect(() => {
    if (userData?.apiKey) {
      setApiKey(userData.apiKey);
    }
  }, [userData]);

  const generateApiKey = async () => {
    if (!currentUser) return;

    setGenerating(true);
    try {
      const createApiKey = httpsCallable(functions, 'users-createApiKey');
      const result = await createApiKey();
      
      if (result.data?.key) {
        setApiKey(result.data.key);
        setShowKey(true);
        toast({
          title: 'API Key Generated',
          description: 'Your new API key has been generated successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate API key. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast({
        title: 'Copied!',
        description: 'API key copied to clipboard.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: 'Copy Failed',
        description: 'Could not copy to clipboard. Please copy manually.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRegenerateClick = () => {
    onAlertOpen();
  };

  const confirmRegenerate = async () => {
    onAlertClose();
    await generateApiKey();
  };

  const maskedKey = apiKey ? `${apiKey.substring(0, 8)}${'*'.repeat(apiKey.length - 12)}${apiKey.substring(apiKey.length - 4)}` : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader color={textColor}>
          <HStack>
            <Text>API Key Management</Text>
            <Badge colorScheme="blue" variant="subtle">MCP Integration</Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Info Alert */}
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>MCP Authentication</AlertTitle>
                <AlertDescription>
                  Use this API key to authenticate your AI agents with Blueprint's MCP (Model Context Protocol) services.
                </AlertDescription>
              </Box>
            </Alert>

            {/* Current API Key Section */}
            <Box>
              <Text fontSize="sm" fontWeight="600" color={textColor} mb={3}>
                Your API Key
              </Text>
              
              {apiKey ? (
                <VStack spacing={3} align="stretch">
                  <InputGroup>
                    <Input
                      value={showKey ? apiKey : maskedKey}
                      isReadOnly
                      bg={codeBg}
                      border="1px solid"
                      borderColor={borderColor}
                      fontFamily="mono"
                      fontSize="sm"
                      color={codeColor}
                    />
                    <InputRightElement width="4.5rem">
                      <HStack spacing={1}>
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          onClick={() => setShowKey(!showKey)}
                          icon={showKey ? <ViewOffIcon /> : <ViewIcon />}
                          variant="ghost"
                          aria-label={showKey ? "Hide API key" : "Show API key"}
                        />
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          onClick={copyToClipboard}
                          icon={<CopyIcon />}
                          variant="ghost"
                          aria-label="Copy API key"
                        />
                      </HStack>
                    </InputRightElement>
                  </InputGroup>
                  
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">
                      Keep this key secure and don't share it publicly
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<RepeatIcon />}
                      onClick={handleRegenerateClick}
                      isLoading={generating}
                      loadingText="Regenerating..."
                    >
                      Regenerate
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <VStack spacing={4}>
                  <Text color="gray.500" textAlign="center">
                    No API key generated yet
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={generateApiKey}
                    isLoading={generating}
                    loadingText="Generating..."
                    leftIcon={generating ? <Spinner size="sm" /> : undefined}
                  >
                    Generate API Key
                  </Button>
                </VStack>
              )}
            </Box>

            <Divider />

            {/* Usage Instructions */}
            <Box>
              <Text fontSize="sm" fontWeight="600" color={textColor} mb={3}>
                Usage Instructions
              </Text>
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  Use these headers when making requests to Blueprint's MCP gateway:
                </Text>
                <Code p={3} borderRadius="md" fontSize="xs" whiteSpace="pre-wrap">
{`X-Blueprint-Agent-Id: your-agent-id
X-Blueprint-API-Key: ${apiKey || 'your-api-key'}`}
                </Code>
                <Text fontSize="xs" color="gray.500">
                  Replace <Code fontSize="xs">your-agent-id</Code> with your actual agent ID from the agents page.
                </Text>
              </VStack>
            </Box>

            {/* MCP Endpoints */}
            <Box>
              <Text fontSize="sm" fontWeight="600" color={textColor} mb={3}>
                Available MCP Endpoints
              </Text>
              <VStack spacing={2} align="stretch">
                <Code fontSize="xs" p={2} borderRadius="md">
                  https://mcp-auth-gateway-333437725100.us-central1.run.app/api/v1/jira/mcp
                </Code>
                <Code fontSize="xs" p={2} borderRadius="md">
                  https://mcp-auth-gateway-333437725100.us-central1.run.app/api/v1/github/mcp
                </Code>
                <Code fontSize="xs" p={2} borderRadius="md">
                  https://mcp-auth-gateway-333437725100.us-central1.run.app/api/v1/asana/mcp
                </Code>
                <Code fontSize="xs" p={2} borderRadius="md">
                  https://mcp-auth-gateway-333437725100.us-central1.run.app/api/v1/google-calendar/mcp
                </Code>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Regenerate API Key
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to regenerate your API key? This will invalidate your current key and may break existing integrations.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onAlertClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={confirmRegenerate} 
                ml={3}
                isLoading={generating}
                loadingText="Regenerating..."
              >
                Regenerate
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default ApiKeyModal;
